import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import shiftService from '../services/shiftService';
import SearchFilter from '../components/SearchFilter';
import { exportMultipleShiftsToPDF, exportToCSV } from '../utils/pdfExport';

function Shifts() {
  const navigate = useNavigate();
  const [shifts, setShifts] = useState([]);
  const [filteredShifts, setFilteredShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadShifts();
  }, []);

  const loadShifts = async () => {
    try {
      const response = await shiftService.getShifts();
      const data = response.data || [];
      setShifts(data);
      setFilteredShifts(data);
    } catch (err) {
      setError('Failed to load shifts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = (filtered) => {
    setFilteredShifts(filtered);
  };

  const handleExportPDF = () => {
    exportMultipleShiftsToPDF(filteredShifts, 'shifts-report.pdf');
  };

  const handleExportCSV = () => {
    const csvData = filteredShifts.map(shift => ({
      'File Name': shift.fileName,
      'Upload Date': new Date(shift.uploadDate).toLocaleDateString(),
      'Staff Count': shift.staffCount || 0,
      'Total Hours': shift.totalHours?.toFixed(1) || 0,
      'Overtime Hours': shift.totalOvertimeHours?.toFixed(1) || 0,
      'Night Work Hours': shift.totalNightWorkHours?.toFixed(1) || 0
    }));
    exportToCSV(csvData, 'shifts-export.csv');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this shift data?')) {
      return;
    }

    try {
      await shiftService.deleteShift(id);
      setShifts(shifts.filter(shift => shift._id !== id));
    } catch (err) {
      alert('Failed to delete shift data');
      console.error(err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="app-container">
      <div className="container">
        <div className="page-header">
          <h1>Shift Records</h1>
          <p>View and manage your uploaded shift schedules</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={() => navigate('/shifts/upload')} className="btn btn-primary">
              📤 Upload New Shift Data
            </button>
            <button onClick={() => navigate('/analytics')} className="btn btn-secondary">
              📊 View Analytics
            </button>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handleExportPDF} className="btn btn-success" disabled={filteredShifts.length === 0}>
              📄 Export PDF
            </button>
            <button onClick={handleExportCSV} className="btn btn-success" disabled={filteredShifts.length === 0}>
              📊 Export CSV
            </button>
          </div>
        </div>

        {shifts.length > 0 && (
          <SearchFilter data={shifts} onFilter={handleFilter} />
        )}

        {filteredShifts.length === 0 && shifts.length > 0 ? (
          <div className="card">
            <p style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
              No shifts match your search criteria. Try adjusting your filters.
            </p>
          </div>
        ) : shifts.length === 0 ? (
          <div className="card">
            <p style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
              No shift data uploaded yet. Click the button above to upload your first shift schedule!
            </p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>File Name</th>
                  <th>Upload Date</th>
                  <th>Staff Count</th>
                  <th>Total Hours</th>
                  <th>Overtime Hours</th>
                  <th>Night Work Hours</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredShifts.map((shift) => (
                  <tr key={shift._id}>
                    <td>{shift.fileName}</td>
                    <td>{formatDate(shift.uploadDate)}</td>
                    <td>{shift.staffCount || 0}</td>
                    <td>{shift.totalHours?.toFixed(1) || 0}</td>
                    <td>{shift.totalOvertimeHours?.toFixed(1) || 0}</td>
                    <td>{shift.totalNightWorkHours?.toFixed(1) || 0}</td>
                    <td className="table-actions">
                      <button
                        onClick={() => navigate(`/shifts/view/${shift._id}`)}
                        className="btn btn-primary btn-sm"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleDelete(shift._id)}
                        className="btn btn-danger btn-sm"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Shifts;
